console.log('🛒 Cart Kit Manager script loaded');

/**
 * Cart Kit Manager v2.1
 * Handles kit grouping, completion tracking, and cart organization for multi-athlete shopping
 * Updated: Fixed product lookup with proper JSON endpoints
 */
class CartKitManager {
  constructor() {

    
    // Prevent duplicate initialization
    if (window.cartKitManagerInitialized) {
      console.log('🛒 CartKitManager already initialized, skipping');
      return;
    }
    window.cartKitManagerInitialized = true;
    
    this.kitsStorageKey = 'athlete_kits';
    this.hasGroupedCollection = false;
    this.currentKit = null;
    this.kits = {};
    
    this.init();
  }

  init() {
    // Check for all-f GUID bypass — hide "Previously Purchased" section on the cart page
    // so bypass users don't see simulated purchases. Uses CSS so render logic is untouched.
    this.checkRequiredItemsBypass();

    // Load kit data from localStorage
    this.loadKitData();
    
    console.log('Has grouped collection:', this.hasGroupedCollection);
    

    
    try {
      // Set up cart navigation event listeners (always needed for Back to Team Shop links)
      this.setupCartNavigationListeners();

    } catch (error) {
      console.error('🛒 Error setting up cart navigation listeners:', error);
    }
    
    // Always try to organize cart to check for grouped items

    this.organizeCartByKits();
  }

  checkRequiredItemsBypass() {
    try {
      const stored = localStorage.getItem('svsCustomer');
      if (!stored) return;
      const data = JSON.parse(stored);
      if (!data || !data.svsCustomerId || typeof data.svsCustomerId !== 'string') return;
      const stripped = data.svsCustomerId.replace(/-/g, '');
      if (stripped.length === 32 && /^f+$/i.test(stripped)) {
        if (!document.getElementById('svs-required-bypass-cart-style')) {
          const style = document.createElement('style');
          style.id = 'svs-required-bypass-cart-style';
          style.textContent = '.cart-kit-previously-purchased { display: none !important; }';
          document.head.appendChild(style);
        }
        console.log('🔓 Cart: hiding Previously Purchased section (all-f GUID bypass)');
      }
    } catch(e) {
      console.warn('Error checking required items bypass in cart:', e);
    }
  }

  loadKitData() {
    try {
      const kitsData = localStorage.getItem(this.kitsStorageKey);
      console.log('Raw localStorage data:', kitsData);
      
      if (kitsData) {
        const parsed = JSON.parse(kitsData);
        this.hasGroupedCollection = parsed.hasGroupedCollection || false;
        this.currentKit = parsed.currentKit;
        
        console.log('Loaded kit data in cart:', parsed);
        console.log('Has grouped collection:', this.hasGroupedCollection);
        console.log('Current kit:', this.currentKit);
      } else {
        console.log('No kit data found in localStorage');
      }
    } catch (e) {
      console.warn('Error loading kit data in cart:', e);
    }
  }

  async organizeCartByKits() {
    console.log('Organizing cart by kits');
    
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      console.log('Cart data:', cart);
      console.log('📦 Detailed cart structure:');
      console.log('📦 Cart items count:', cart.items?.length || 0);

      if (cart.items && cart.items.length > 0) {
        console.log('📦 First item detailed structure:', JSON.stringify(cart.items[0], null, 2));
      }

      // Cross-collection validation
      const collectionMismatch = this.validateCollectionConsistency(cart.items || []);
      if (collectionMismatch) {
        this.showCollectionMismatchError(collectionMismatch);
        this.disableCheckout();
        return;
      } else {
        this.enableCheckout();
      }

      const groups = this.groupItemsByKit(cart.items || []);
      
      // Only show kit sections if there are actually grouped items
      const hasGroupedItems = Object.keys(groups.kitGroups).length > 0;
      console.log('🔍 Has grouped items in cart:', hasGroupedItems);
      
      if (hasGroupedItems) {
        console.log('✅ Found grouped items - showing kit organization');
        await this.renderKitSections(groups, cart);
        this.showKitManagementUI();
      } else {
        console.log('ℹ️ No grouped items found - using legacy cart display');
        // Remove any existing kit sections
        this.removeKitSections();
      }
    } catch (error) {
      console.error('Error organizing cart by kits:', error);
    }
  }

  groupItemsByKit(items) {
    console.log('🔍 Grouping items by kit...');
    console.log('🔍 Total cart items:', items.length);
    
    const kitGroups = {};
    const legacyItems = [];
    
    items.forEach((item, index) => {
      console.log(`🔍 Item ${index + 1}:`, item.product_title);
      console.log(`🔍 Item ${index + 1} properties:`, item.properties);
      
      // Check for kit ID in various formats
      const kitGroupName = item.properties?._kit_group_name || 
                          item.properties?.kit_group_name;
      
      console.log(`🔍 Item ${index + 1} kit group name:`, kitGroupName);
      
      if (kitGroupName) {
        console.log(`✅ Item ${index + 1} has kit group name, adding to group: ${kitGroupName}`);
        
        if (!kitGroups[kitGroupName]) {
          kitGroups[kitGroupName] = {
            name: kitGroupName,
            requiredItems: [],
            regularItems: [],
            complete: false
          };
        }
        
        // Categorize items (simplified for now)
        const isRequired = item.product_type === 'Required Items';
        if (isRequired) {
          kitGroups[kitGroupName].requiredItems.push(item);
        } else {
          kitGroups[kitGroupName].regularItems.push(item);
        }
        
        // Check completion status
        this.checkKitCompletion(kitGroups[kitGroupName]);
      } else {
        console.log(`❌ Item ${index + 1} has no kit group name, adding to legacy items`);
        legacyItems.push(item);
      }
    });
    
    console.log('🔍 Final kit groups:', kitGroups);
    console.log('🔍 Final legacy items:', legacyItems.length);
    
    return { kitGroups, legacyItems };
  }

  checkKitCompletion(kit) {
    // For now, just check if there are required items
    // In a full implementation, this would check against the actual required products list
    return kit.requiredItems.length > 0;
  }

  async renderKitSections(groups, cart) {
    console.log('Rendering kit sections...');
    console.log('Kit groups to render:', groups);
    
    // Check if kit sections already exist
    const existingKitSections = document.querySelector('.cart-kit-sections');
    if (existingKitSections) {
      console.log('🛒 Kit sections already exist, removing old ones');
      existingKitSections.remove();
    }
    
    const cartItemsContainer = document.querySelector('.cart__items, .cart-items');
    console.log('Cart items container found:', !!cartItemsContainer);
    console.log('Cart items container:', cartItemsContainer);
    
    if (!cartItemsContainer) {
      console.warn('Cart items container not found, trying alternative selectors...');
      
      // Try alternative selectors
      const alternatives = [
        'cart-items',
        '.cart-drawer__items',
        '.cart',
        '[data-cart-items]',
        '.shopify-section'
      ];
      
      for (const selector of alternatives) {
        const element = document.querySelector(selector);
        console.log(`Selector "${selector}":`, !!element, element);
        if (element) {
          console.log('Using alternative container:', selector);
          break;
        }
      }
      return;
    }
    
    // Create kit sections HTML
    let kitSectionsHTML = '';
    
    console.log('Building kit sections HTML...');
    
    // Render kit groups asynchronously
    for (const kit of Object.values(groups.kitGroups)) {
      console.log('Rendering kit:', kit.name);
      kitSectionsHTML += await this.renderKitSection(kit);
    }
    
    // Render legacy items if any
    if (groups.legacyItems.length > 0) {
      console.log('Rendering legacy items:', groups.legacyItems.length);
      kitSectionsHTML += this.renderLegacySection(groups.legacyItems);
    }
    
    // Check if all kits are complete before rendering management section
    const kitCompletionChecks = await Promise.all(
      Object.values(groups.kitGroups).map(async kit => {
        const missingCount = await this.getMissingItemsCount(kit);
        return missingCount === 0;
      })
    );
    const allKitsComplete = kitCompletionChecks.every(isComplete => isComplete);
    
    // Add kit management UI
    kitSectionsHTML += this.renderKitManagementSection(allKitsComplete);
    
    console.log('Final kit sections HTML:', kitSectionsHTML);
    
    // Insert kit sections before existing cart items
    const kitContainer = document.createElement('div');
    kitContainer.className = 'cart-kit-sections';
    kitContainer.innerHTML = kitSectionsHTML;
    
    console.log('Inserting kit container into DOM...');
    
    // Try multiple insertion points in order of preference
    const insertionTargets = [
      document.querySelector('main.cart'),
      document.querySelector('.cart'),
      document.querySelector('[data-cart-main]'),
      document.querySelector('.main-content'),
      document.querySelector('main'),
      document.querySelector('#main'),
      document.body // Final fallback
    ];
    
    let inserted = false;
    for (const target of insertionTargets) {
      if (target && target !== document.body) {
        target.insertBefore(kitContainer, target.firstChild);
        console.log(`✅ Kit sections inserted in:`, target.tagName, target.className);
        inserted = true;
        break;
      }
    }
    
    // Final fallback to body if nothing else worked
    if (!inserted) {
      document.body.insertBefore(kitContainer, document.body.firstChild);
      console.log('✅ Kit sections inserted at top of body (fallback)');
    }
    
    console.log('Kit sections rendered successfully');
    
    // Debug: Check if the kit sections are actually visible
    setTimeout(() => {
      console.log('🔍 DOM DEBUG: Checking if kit sections are visible...');
      const insertedKitSections = document.querySelector('.cart-kit-sections');
      if (insertedKitSections) {
        console.log('✅ Kit sections found in DOM:', insertedKitSections);
        const dimensions = {
          width: insertedKitSections.offsetWidth,
          height: insertedKitSections.offsetHeight,
          display: getComputedStyle(insertedKitSections).display,
          visibility: getComputedStyle(insertedKitSections).visibility,
          opacity: getComputedStyle(insertedKitSections).opacity
        };
        console.log('📐 Kit sections dimensions:', dimensions);
      } else {
        console.log('❌ Kit sections NOT found in DOM!');
      }
    }, 100);
  }

  async renderKitSection(kit) {
    console.log('🎨 Rendering kit section for:', kit.name);
    console.log('🎨 Kit structure:', kit);
    
    const missingCount = await this.getMissingItemsCount(kit);
    const isComplete = kit.complete || missingCount === 0;
    const statusIcon = isComplete ? '✅' : '⚠️';
    
    // Combine required and regular items
    const allItems = [...(kit.requiredItems || []), ...(kit.regularItems || [])];
    console.log('🎨 All items in kit:', allItems.length);
    
    // Get previously purchased items that fulfill required products for this kit
    const previouslyPurchasedItems = await this.getPreviouslyPurchasedItemsForKit(kit);
    
    let missingItemsSection = '';
    let previouslyPurchasedSection = '';
    
    // Add previously purchased items section if any exist
    if (previouslyPurchasedItems.length > 0) {
      previouslyPurchasedSection = `
        <div class="cart-kit-items cart-kit-previously-purchased">
          <h4 class="cart-kit-items-title">🛍️ Previously Purchased:</h4>
          ${previouslyPurchasedItems.map(item => this.renderPreviouslyPurchasedItem(item)).join('')}
        </div>
      `;
    }
    
    if (!isComplete) {
      // Get missing product details
      const missingProducts = await this.getMissingProductDetails(kit);
      
      if (missingProducts.length > 0) {
        missingItemsSection = `
          <div class="cart-kit-missing-items">
            <h4 class="cart-kit-missing-title">⚠️ Still Needed:</h4>
            <div class="cart-kit-missing-list">
              ${missingProducts.map(product => this.renderMissingItem(product, kit)).join('')}
            </div>
          </div>
        `;
      } else {
        // Improved fallback when we can't fetch product details
        missingItemsSection = `
          <div class="cart-kit-missing-items">
            <div class="cart-kit-missing-fallback">
              <h4 class="cart-kit-missing-title">⚠️ Still Needed:</h4>
              <p class="cart-kit-missing-description">Complete your ${kit.name} kit by adding ${missingCount} more required item${missingCount > 1 ? 's' : ''}.</p>
              <a href="#" class="cart-kit-missing-shop-link back-to-team-shop-link">
                <span class="cart-kit-missing-shop-icon">🏪</span>
                <span class="cart-kit-missing-shop-text">Visit Team Shop</span>
              </a>
            </div>
          </div>
        `;
      }
    }
    
    const statusText = isComplete ? 'Kit Complete' : `${missingCount} items needed to complete`;
    
    return `
      <div class="cart-kit-section" data-kit-group="${kit.name}">
        <div class="cart-kit-header">
          <h3 class="cart-kit-title">${kit.name} Team Kit</h3>
          <span class="cart-kit-status ${isComplete ? 'complete' : 'incomplete'}">${statusIcon} ${statusText}</span>
        </div>
        ${allItems.length > 0 ? `
        <div class="cart-kit-items">
          <h4 class="cart-kit-items-title">✅ Added to Kit:</h4>
          ${allItems.map(item => this.renderKitItem(item)).join('')}
        </div>
        ` : ''}
        ${previouslyPurchasedSection}
        ${missingItemsSection}
      </div>
    `;
  }

  renderMissingItem(product, kit) {
    const productUrl = product.url && product.url !== '#' ? product.url : null;
    const groupKey = kit.groupKey || kit.name;
    const lastVisitedCollection = localStorage.getItem('lastVisitedCollection');
    
    // Build the final URL with group context
    let finalUrl = '#';
    if (productUrl) {
      finalUrl = `${productUrl}?groupKey=${encodeURIComponent(groupKey)}&collection=${lastVisitedCollection || ''}`;
    }
    
    const linkClass = productUrl ? 'cart-kit-missing-product-link' : 'cart-kit-missing-product-link back-to-team-shop-link';
    
    return `
      <div class="cart-kit-missing-product">
        <a href="${finalUrl}" class="${linkClass}">
          <div class="cart-kit-missing-product-media">
            ${product.image ? 
              `<img src="${product.image}" alt="${product.title}" class="cart-kit-missing-product-image" loading="lazy">` : 
              `<div class="cart-kit-missing-product-placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cart-kit-missing-product-icon">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="9" cy="9" r="2"/>
                  <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
                </svg>
              </div>`
            }
          </div>
          <div class="cart-kit-missing-product-details">
            <h4 class="cart-kit-missing-product-title">${product.title}</h4>
            <p class="cart-kit-missing-product-status">Required Item</p>
          </div>
        </a>
      </div>
    `;
  }

  renderKitItem(item) {
    const imageUrl = item.featured_image?.url || item.image;
    const productUrl = item.url || `/products/${item.handle}`;
    
    return `
      <div class="cart-kit-item cart-kit-item-added">
        <div class="cart-kit-item-status">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cart-kit-item-check">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        </div>
        <div class="cart-kit-item-media">
          ${imageUrl ? 
            `<img src="${imageUrl}" alt="${item.product_title}" class="cart-kit-item-image">` :
            `<div class="cart-kit-item-placeholder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cart-kit-item-icon">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="9" cy="9" r="2"/>
                <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
              </svg>
            </div>`
          }
        </div>
        <div class="cart-kit-item-details">
          <a href="${productUrl}" class="cart-kit-item-link">
            <h4 class="cart-kit-item-name">${item.product_title}</h4>
          </a>
          <p class="cart-kit-item-type">${item.product_type}</p>
          <p class="cart-kit-item-quantity">Qty: ${item.quantity}</p>
        </div>
      </div>
    `;
  }

  renderLegacySection(items) {
    return `
      <div class="cart-legacy-section">
        <div class="cart-legacy-header">
          <h3 class="cart-legacy-title">Other Items</h3>
        </div>
        <div class="cart-legacy-items">
          ${items.map(item => this.renderKitItem(item)).join('')}
        </div>
      </div>
    `;
  }

  renderKitManagementSection(allKitsComplete = false) {
    // Check if svsCustomer exists in localStorage (existing system)
    let svsCustomer = null;
    try {
      const svsCustomerData = localStorage.getItem('svsCustomer');
      if (svsCustomerData) {
        svsCustomer = JSON.parse(svsCustomerData);
      }
    } catch (error) {
      console.error('Error parsing svsCustomer from localStorage:', error);
    }
    
    const hasSvsCustomerId = svsCustomer && svsCustomer.svsCustomerId;
    console.log('Checking existing svsCustomer for management section:', hasSvsCustomerId);
    console.log('All kits complete:', allKitsComplete);
    
    if (hasSvsCustomerId) {
      console.log('svsCustomerId present in localStorage - showing single athlete mode');
      if (allKitsComplete) {
        return `
          <div class="cart-kit-management">
            <p class="single-athlete-mode">✅ All required items added! Complete your order when ready.</p>
          </div>
        `;
      } else {
        return `
          <div class="cart-kit-management">
            <p class="single-athlete-mode incomplete-kit">⚠️ Please add all required items to complete your kit before checkout.</p>
          </div>
        `;
      }
    } else {
      if (allKitsComplete) {
        return `
          <div class="cart-kit-management">
            <button class="add-another-athlete-btn">+ Shop for Another Athlete</button>
          </div>
        `;
      } else {
        // Don't show the "Add another athlete" button until all kits are complete
        // The kit status is now clearly communicated through the individual kit sections
        return '';
      }
    }
  }

  removeKitSections() {
    console.log('🧹 Removing existing kit sections');
    const existingKitSections = document.querySelector('.cart-kit-sections');
    if (existingKitSections) {
      existingKitSections.remove();
      console.log('✅ Kit sections removed');
    }
  }

  setupCartNavigationListeners() {
    // Add event listeners for cart navigation (always needed)
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('back-to-team-shop-link')) {
        e.preventDefault();
        this.handleBackToTeamShop();
      }
      
      // Also check if we clicked inside a back-to-team-shop-link (for nested elements)
      const backToShopLink = e.target.closest('.back-to-team-shop-link');
      if (backToShopLink && !e.target.classList.contains('back-to-team-shop-link')) {
        e.preventDefault();
        this.handleBackToTeamShop();
      }
      
      if (e.target.classList.contains('add-another-athlete-btn')) {
        e.preventDefault();
        this.handleAddAnotherAthlete();
      }
    });
  }

  showKitManagementUI() {
    // This method is now just for kit-specific UI setup if needed
    console.log('Kit management UI shown');
  }

  handleAddAnotherAthlete() {
    console.log('Add another athlete clicked');
    
    // Use the existing clearKitAndStartNew functionality from CollectionGroupsManager
    if (window.collectionGroupsManager && typeof window.collectionGroupsManager.clearKitAndStartNew === 'function') {
      // Clear current kit and show group selection modal
      window.collectionGroupsManager.clearKitAndStartNew();
    } else {
      // Fallback: Clear local storage manually and navigate
      console.log('CollectionGroupsManager not available, clearing storage manually');
      try {
        localStorage.removeItem('athlete_kits');
        localStorage.removeItem('collection_selected_group');
        localStorage.removeItem('svsCustomer'); // Also clear svsCustomer data
        
        // Hide player card since svsCustomer data is now cleared
        const playerCardSection = document.getElementById('PlayerCardSectionInline');
        if (playerCardSection) {
          playerCardSection.style.display = 'none';
          console.log('✅ Player card hidden (fallback)');
        }
      } catch (e) {
        console.warn('Error clearing localStorage:', e);
      }
      
      // Try to get the stored collection URL, otherwise go to home page
      const lastVisitedCollection = localStorage.getItem('lastVisitedCollection');
      const redirectUrl = lastVisitedCollection ? `/collections/${lastVisitedCollection}` : '/';
      
      console.log('Redirecting to:', redirectUrl);
      window.location.href = redirectUrl;
    }
  }

  handleBackToTeamShop() {
    console.log('Back to team shop clicked');
    
    // Try to get the stored collection URL first
    const lastVisitedCollection = localStorage.getItem('lastVisitedCollection');
    
    if (lastVisitedCollection) {
      const redirectUrl = `/collections/${lastVisitedCollection}`;
      console.log('Redirecting to stored collection:', redirectUrl);
      window.location.href = redirectUrl;
    } else {
      // Fallback to home page which will handle collection routing
      console.log('No stored collection, redirecting to home page');
      window.location.href = '/';
    }
  }

  async getMissingItemsCount(kit) {
    const missingItems = await this.getMissingRequiredItems(kit);
    return missingItems.length;
  }

  async getPreviouslyPurchasedProductIds() {
    console.log('🔍 DEBUG: getPreviouslyPurchasedProductIds called');
    try {
      const svsCustomerData = localStorage.getItem('svsCustomer');
      console.log('🔍 DEBUG: svsCustomerData from localStorage:', svsCustomerData);
      
      if (svsCustomerData) {
        const customerData = JSON.parse(svsCustomerData);
        console.log('🔍 DEBUG: Parsed customerData:', customerData);
        
        if (customerData && customerData.purchased_products && Array.isArray(customerData.purchased_products)) {
          const productIds = customerData.purchased_products.map(id => id.toString());
          console.log('🔍 DEBUG: Previously purchased product IDs from localStorage:', productIds);
          return productIds;
        }
      }

      // Fallback: check cart attributes
      console.log('🔍 DEBUG: No svsCustomerData found in localStorage, checking cart attributes...');
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        console.log('🔍 DEBUG: Cart attributes:', cart.attributes);
        
        if (cart.attributes && cart.attributes.previously_purchased_products) {
          console.log('🔍 DEBUG: Found previously_purchased_products in cart attributes:', cart.attributes.previously_purchased_products);
          const parsedProducts = JSON.parse(cart.attributes.previously_purchased_products);
          console.log('🔍 DEBUG: Parsed previously purchased products from cart:', parsedProducts);
          
          // Remove duplicates and convert to strings
          const uniqueProductIds = [...new Set(parsedProducts)].map(id => id.toString());
          console.log('🔍 DEBUG: Unique previously purchased product IDs from cart attributes:', uniqueProductIds);
          return uniqueProductIds;
        } else {
          console.log('🔍 DEBUG: No previously_purchased_products found in cart attributes');
        }
      } catch (cartError) {
        console.warn('🔍 DEBUG: Error fetching cart for previously purchased products:', cartError);
      }
      
    } catch (error) {
      console.warn('🔍 DEBUG: Error reading previously purchased products:', error);
    }
    
    return [];
  }

  debugCartAttributes() {
    console.log('🔍 DEBUG: Checking cart attributes...');
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('🔍 DEBUG: Cart attributes:', cart.attributes);
        if (cart.attributes && cart.attributes.previously_purchased_products) {
          console.log('🔍 DEBUG: Found previously_purchased_products in cart attributes:', cart.attributes.previously_purchased_products);
          try {
            const parsedProducts = JSON.parse(cart.attributes.previously_purchased_products);
            console.log('🔍 DEBUG: Parsed previously purchased products from cart:', parsedProducts);
          } catch (e) {
            console.warn('🔍 DEBUG: Error parsing previously_purchased_products from cart:', e);
          }
        } else {
          console.log('🔍 DEBUG: No previously_purchased_products found in cart attributes');
        }
      })
      .catch(error => {
        console.warn('🔍 DEBUG: Error fetching cart:', error);
      });
  }

  async getPreviouslyPurchasedCountForKit(kit) {
    const previouslyPurchasedProductIds = await this.getPreviouslyPurchasedProductIds();
    if (previouslyPurchasedProductIds.length === 0) {
      return 0;
    }

    // Get all required product IDs for this kit
    const allRequiredProductIds = new Set();
    [...kit.requiredItems, ...kit.regularItems].forEach((item) => {
      const requiredProducts = item.properties?._required_items_for_cart || '';
      if (requiredProducts) {
        requiredProducts.split(',').forEach(id => {
          if (id.trim()) {
            allRequiredProductIds.add(id.trim());
          }
        });
      }
    });

    // Count how many previously purchased items are required for this kit
    const relevantPreviouslyPurchasedCount = previouslyPurchasedProductIds.filter(id => 
      allRequiredProductIds.has(id.toString())
    ).length;

    console.log('🔍 DEBUG: Previously purchased count for kit:', relevantPreviouslyPurchasedCount);
    return relevantPreviouslyPurchasedCount;
  }

  async getPreviouslyPurchasedItemsForKit(kit) {
    const previouslyPurchasedProductIds = await this.getPreviouslyPurchasedProductIds();
    if (previouslyPurchasedProductIds.length === 0) {
      return [];
    }

    // Get all required product IDs for this kit
    const allRequiredProductIds = new Set();
    [...kit.requiredItems, ...kit.regularItems].forEach((item) => {
      const requiredProducts = item.properties?._required_items_for_cart || '';
      if (requiredProducts) {
        requiredProducts.split(',').forEach(id => {
          if (id.trim()) {
            allRequiredProductIds.add(id.trim());
          }
        });
      }
    });

    // Find previously purchased items that are also required for this kit
    const relevantPreviouslyPurchasedIds = previouslyPurchasedProductIds.filter(id => 
      allRequiredProductIds.has(id.toString())
    );

    if (relevantPreviouslyPurchasedIds.length === 0) {
      return [];
    }

    console.log('🔍 DEBUG: Relevant previously purchased IDs for kit:', relevantPreviouslyPurchasedIds);

    // Fetch product details for previously purchased items
    const productDetails = [];
    try {
      for (const productId of relevantPreviouslyPurchasedIds) {
        console.log('🔍 DEBUG: Looking up product details for previously purchased ID:', productId);
        
        // Try to find product info using the existing findProductInfoById method
        let productInfo = await this.findProductInfoById(productId);
        
        if (productInfo) {
          console.log('✅ DEBUG: Found product info for previously purchased item:', productInfo.title);
          productDetails.push(productInfo);
        } else {
          console.log('❌ DEBUG: No product info found for previously purchased item, using fallback');
          // Fallback: create basic product info
          productDetails.push({
            id: productId,
            title: `Previously Purchased Item ${productId}`,
            handle: `product-${productId}`,
            price: null,
            image: null,
            type: 'Previously Purchased',
            url: '#'
          });
        }
      }
    } catch (error) {
      console.warn('🔍 DEBUG: Error fetching previously purchased product details:', error);
    }

    console.log('🔍 DEBUG: Final previously purchased items for kit:', productDetails);
    return productDetails;
  }

  renderPreviouslyPurchasedItem(item) {
    const imageUrl = item.image;
    const productUrl = item.url || `/products/${item.handle}`;
    
    return `
      <div class="cart-kit-item cart-kit-item-previously-purchased">
        <div class="cart-kit-item-status cart-kit-item-status-purchased">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cart-kit-item-check">
            <path d="M9 12l2 2 4-4"/>
            <circle cx="12" cy="12" r="10"/>
          </svg>
        </div>
        <div class="cart-kit-item-media">
          ${imageUrl ? 
            `<img src="${imageUrl}" alt="${item.title}" class="cart-kit-item-image">` :
            `<div class="cart-kit-item-placeholder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="cart-kit-item-icon">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="9" cy="9" r="2"/>
                <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
              </svg>
            </div>`
          }
        </div>
        <div class="cart-kit-item-details">
          <a href="${productUrl}" class="cart-kit-item-link">
            <h4 class="cart-kit-item-name">${item.title}</h4>
          </a>
          <p class="cart-kit-item-type">${item.type || 'Product'}</p>
          <p class="cart-kit-item-quantity cart-kit-item-purchased-badge">Previously Purchased</p>
        </div>
      </div>
    `;
  }
    
  async getMissingRequiredItems(kit) {
    // Get all required product IDs that should be in this kit
    const allRequiredProductIds = new Set();
    
    // Collect required products from all items in this kit
    [...kit.requiredItems, ...kit.regularItems].forEach((item) => {
      const requiredProducts = item.properties?._required_items_for_cart || '';
      
      if (requiredProducts) {
        requiredProducts.split(',').forEach(id => {
          if (id.trim()) {
            allRequiredProductIds.add(id.trim());
          }
        });
      }
    });
    
    // If no required products are defined, consider the kit complete
    if (allRequiredProductIds.size === 0) {
      return [];
    }
    
    // Get all cart product IDs for this kit
    const allCartProductIds = [...kit.requiredItems, ...kit.regularItems].map(item => item.product_id.toString());
    
    // Get previously purchased product IDs from svsCustomer data
    const previouslyPurchasedProductIds = await this.getPreviouslyPurchasedProductIds();
    
    // Combine cart items and previously purchased items as "fulfilled"
    const fulfilledProductIds = [...allCartProductIds, ...previouslyPurchasedProductIds];
    
    console.log('🔍 DEBUG: All required product IDs:', Array.from(allRequiredProductIds));
    console.log('🔍 DEBUG: Cart product IDs:', allCartProductIds);
    console.log('🔍 DEBUG: Previously purchased IDs:', previouslyPurchasedProductIds);
    console.log('🔍 DEBUG: All fulfilled IDs:', fulfilledProductIds);
    
    // Find missing required products (required items that are neither in cart nor previously purchased)
    const missingProductIds = Array.from(allRequiredProductIds).filter(requiredId => 
      !fulfilledProductIds.includes(requiredId.toString())
    );
    
    if (missingProductIds.length > 0) {
      console.log('🔍 DEBUG: Missing required items:', missingProductIds.length, missingProductIds);
      console.log('🔍 DEBUG: Previously purchased items considered:', previouslyPurchasedProductIds.length);
    }
    return missingProductIds;
  }

  async getMissingProductDetails(kit) {
    console.log('🔍 NEW getMissingProductDetails v2.1 called');
    const missingProductIds = await this.getMissingRequiredItems(kit);
    
    if (missingProductIds.length === 0) {
      return [];
    }

    try {
      console.log('🔍 Looking up product details for IDs:', missingProductIds);
      const productDetails = [];
      
      for (const productId of missingProductIds) {
        console.log('🔍 Searching for product ID:', productId);
        let productInfo = await this.findProductInfoById(productId);
        
        if (productInfo) {
          console.log('✅ Found product info:', productInfo.title);
        } else {
          console.log('❌ No product info found, using fallback');
          // Since cart page doesn't have product data, use fallback that navigates to team shop
          productInfo = {
            id: productId,
            title: 'Required Item',
            handle: null,
            url: '#',
            price: null,
            image: null,
            type: 'Required Items'
          };
        }
        
        productDetails.push(productInfo);
      }
      return productDetails;
    } catch (error) {
      console.error('Error getting missing product details:', error);
      return [];
    }
  }

  async findProductInfoById(productId) {
    try {
      console.log('🔍 findProductInfoById called with ID:', productId);
      
      // ONLY source: cached product details from collection page
      console.log('🔍 Checking cached product details for ID:', productId);
      const productCache = localStorage.getItem('required_products_cache');
      if (productCache) {
        const cache = JSON.parse(productCache);
        const cachedProduct = cache[productId.toString()];
        
        if (cachedProduct) {
          console.log('✅ Found product details in cache:', cachedProduct.title);
          return {
            id: cachedProduct.id,
            title: cachedProduct.title,
            handle: cachedProduct.handle,
            url: cachedProduct.url,
            price: cachedProduct.price,
            image: cachedProduct.image,
            type: cachedProduct.type || 'Required Items'
          };
        } else {
          console.log('❌ Product ID', productId, 'not found in cache');
          console.log('❌ Available cached products:', Object.keys(cache));
        }
      } else {
        console.log('❌ No product cache available - must visit collection page first');
      }

      return null;
    } catch (error) {
      console.warn('Error finding product info for ID:', productId, error);
      return null;
    }
  }

  /**
   * Check that all cart items belong to the same collection.
   * Returns null if consistent, or { collections: [...] } if mismatched.
   */
  validateCollectionConsistency(items) {
    const collections = new Set();
    items.forEach(item => {
      const col = item.properties?._collection;
      if (col) collections.add(col);
    });

    if (collections.size > 1) {
      console.warn('🚫 Cross-collection items detected:', [...collections]);
      return { collections: [...collections] };
    }
    return null;
  }

  /**
   * Show error banner when cart contains items from multiple collections
   */
  showCollectionMismatchError(mismatch) {
    // Remove existing error if any
    const existing = document.querySelector('.cart-collection-error');
    if (existing) existing.remove();

    const errorDiv = document.createElement('div');
    errorDiv.className = 'cart-collection-error';
    errorDiv.setAttribute('role', 'alert');
    errorDiv.style.cssText = 'padding:16px;margin:0 0 16px;background:#fef2f2;border:2px solid #ef4444;border-radius:8px;';
    errorDiv.innerHTML = `
      <p style="margin:0 0 8px;color:#991b1b;font-weight:600;">Your cart contains items from multiple team shops.</p>
      <p style="margin:0 0 12px;color:#991b1b;">You must remove items so that only one team shop remains before you can checkout.</p>
      <div style="display:flex;flex-wrap:wrap;gap:8px;">
        ${mismatch.collections.map(col => `
          <button class="remove-collection-items-btn" data-collection="${col}"
            style="padding:8px 16px;background:#ef4444;color:white;border:none;border-radius:4px;cursor:pointer;">
            Remove items from "${col}"
          </button>
        `).join('')}
      </div>
    `;

    // Insert at top of cart items
    const cartItems = document.querySelector('.cart__items') || document.querySelector('cart-items');
    if (cartItems) {
      cartItems.parentNode.insertBefore(errorDiv, cartItems);
    } else {
      document.querySelector('main')?.prepend(errorDiv);
    }

    // Bind remove buttons
    errorDiv.querySelectorAll('.remove-collection-items-btn').forEach(btn => {
      btn.addEventListener('click', () => this.removeItemsByCollection(btn.dataset.collection));
    });
  }

  /**
   * Disable all checkout buttons
   */
  disableCheckout() {
    const checkoutBtn = document.getElementById('checkout');
    if (checkoutBtn) {
      checkoutBtn.disabled = true;
      checkoutBtn.title = 'Please resolve cart issues before checkout';
    }

    const dynamicBtns = document.querySelector('.additional-checkout-buttons');
    if (dynamicBtns) {
      dynamicBtns.style.display = 'none';
    }
  }

  /**
   * Re-enable checkout buttons
   */
  enableCheckout() {
    const checkoutBtn = document.getElementById('checkout');
    if (checkoutBtn) {
      checkoutBtn.disabled = false;
      checkoutBtn.title = '';
    }

    const dynamicBtns = document.querySelector('.additional-checkout-buttons');
    if (dynamicBtns) {
      dynamicBtns.style.display = '';
    }
  }

  /**
   * Remove all cart items belonging to a specific collection
   */
  async removeItemsByCollection(collectionHandle) {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const updates = {};
      cart.items.forEach(item => {
        if (item.properties?._collection === collectionHandle) {
          updates[item.key] = 0;
        }
      });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      // Reload the page to reflect changes
      location.reload();
    } catch (error) {
      console.error('Error removing items by collection:', error);
    }
  }

}

// Initialize on cart page only
if (window.location.pathname === '/cart' && !window.cartKitManager) {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!window.cartKitManager) {
      window.cartKitManager = new CartKitManager();
      }
    });
  } else {
    window.cartKitManager = new CartKitManager();
  }
} 